import {
	Accordion,
	AccordionContent,
	AccordionItem,
	AccordionTrigger,
} from "@workspace/ui/components/accordion";
import { Badge } from "@workspace/ui/components/badge";
import { Button } from "@workspace/ui/components/button";
import { FormattedPrice } from "@workspace/ui/components/formatted-price";
import { Separator } from "@workspace/ui/components/separator";
import { format } from "date-fns";
import { nb } from "date-fns/locale";
import { Check, Copy, Loader2, Printer, ShoppingCart } from "lucide-react";
import { useCallback, useState } from "react";
import { useCart } from "~/components/cart/cart-provider";
import CrystallizeResponsiveImage from "~/components/product/crystallize-responsive-image";
import ProductExtrasSelector from "~/components/product/extra-selector";
import { VariantSelector } from "~/components/product/variant-selector";
import QuantitySelector from "~/components/quantity-selector";
import type { DataForProductPage } from "~/routes/storefront/products/splat";
import ProductExtrasSelectorV2 from "../product/extras-selector-v2";

export default function ProductDetailPage({
	data,
}: {
	data: DataForProductPage;
}) {
	const {
		selectedVariant,
		availableFrom,
		product,
		extras,
		isPurchaseable,
		services,
		uniqueAllergens,
	} = data;
	const [selectedExtras, setSelectedExtras] = useState<
		{ name: string; sku: string; price: number; quantity?: number }[]
	>([]);
	const [hasCopiedMenuContents, setHasCopiedMenuContents] = useState(false);
	const { updateCart, isLoading } = useCart();

	const handleExtraSelection = useCallback(
		(
			selectedExtras: {
				name: string;
				sku: string;
				price: number;
				quantity?: number;
			}[],
		) => {
			setSelectedExtras(selectedExtras);
		},
		[],
	);
	const [quantity, setQuantity] = useState(product.minimumQuantity ?? 1);

	const handleAddToCart = (sku: string, quantity: number) => {
		const extrasToAdd = selectedExtras.map((extra) => ({
			sku: extra.sku,
			quantity: extra.maximumQuantity ?? quantity,
		}));

		updateCart(sku, quantity, "update", true, extrasToAdd);
	};

	const accordionDefaultValues: string[] = [];

	const hasExtras = !!extras?.length;
	const hasServices = !!services?.length;
	const hasDishes = !!selectedVariant.dishes?.variants?.length;
	const hasAllergens = !!uniqueAllergens?.length;
	const formattedAvailableFrom = availableFrom
		? format(new Date(availableFrom), "d. MMMM", { locale: nb })
		: undefined;

	if (hasExtras) accordionDefaultValues.push("extras");
	if (hasServices) accordionDefaultValues.push("services");
	if (hasDishes) accordionDefaultValues.push("dishes");

	if (!hasExtras && !hasServices && !hasDishes && hasAllergens) {
		accordionDefaultValues.push("allergens");
	}

	const buildMenuClipboardText = () => {
		const lines: string[] = [product.name];

		if (selectedVariant.name && selectedVariant.name !== product.name) {
			lines.push(`Variant: ${selectedVariant.name}`);
		}

		if (formattedAvailableFrom) {
			lines.push(`Tilgjengelig fra ${formattedAvailableFrom}`);
		}

		if (product.brief) {
			lines.push("", product.brief);
		}

		if (product.minimumQuantity && product.minimumQuantity > 1) {
			lines.push("", `Minimum antall kuverter: ${product.minimumQuantity}`);
		}

		const dishes = selectedVariant.dishes?.variants?.filter(Boolean) ?? [];
		if (dishes.length > 0) {
			lines.push("", "Inkluderte retter:");

			for (const dish of dishes) {
				lines.push(`- ${dish.name}`);

				const dishAllergens =
					dish.product?.foodProperties?.allergens?.allergens?.items
						?.map((allergen) => allergen.name)
						.filter(Boolean) ?? [];

				if (dishAllergens.length > 0) {
					lines.push(`  Allergener: ${dishAllergens.join(", ")}`);
				}
			}
		}

		const allergenNames =
			uniqueAllergens?.map((allergen) => allergen.name).filter(Boolean) ?? [];
		if (allergenNames.length > 0) {
			lines.push("", `Allergener: ${allergenNames.join(", ")}`);
		}

		return lines.join("\n");
	};

	const copyTextToClipboard = async (text: string) => {
		if (navigator.clipboard) {
			try {
				await navigator.clipboard.writeText(text);
				return;
			} catch {}
		}

		const textarea = document.createElement("textarea");
		textarea.value = text;
		textarea.setAttribute("readonly", "");
		textarea.style.position = "fixed";
		textarea.style.left = "-9999px";
		document.body.appendChild(textarea);
		textarea.select();
		document.execCommand("copy");
		document.body.removeChild(textarea);
	};

	const handleCopyMenuContents = async () => {
		await copyTextToClipboard(buildMenuClipboardText());
		setHasCopiedMenuContents(true);
		window.setTimeout(() => setHasCopiedMenuContents(false), 2000);
	};

	return (
		<div className="product-print-page container mx-auto px-4 py-4 md:py-8">
			<div className="product-print-layout grid gap-8 md:grid-cols-2">
				{selectedVariant.images && (
					<div className="product-print-image relative aspect-square overflow-hidden rounded-lg">
						<CrystallizeResponsiveImage
							image={selectedVariant.images?.[0]}
							className="h-full w-full object-cover"
							fetchPriority="high"
							sizes="(min-width: 1024px) 50vw, 100vw"
						/>
					</div>
				)}
				<div className="flex flex-col gap-4">
					<h1 className="font-light text-5xl text-primary tracking-tighter">
						{product.name}
					</h1>
					{availableFrom && (
						<p className="text-gray-600 text-lg">
							{/* TODO: Remove the usage of date-fns in favor of temporal */}
							Tilgjengelig fra {formattedAvailableFrom}
						</p>
					)}

					{product.brief && (
						<p className="text-lg text-primary">{product.brief}</p>
					)}

					<div className="flex items-center gap-2">
						{selectedVariant.defaultPrice != null &&
							selectedVariant.defaultPriceForEveryone != null &&
							selectedVariant.defaultPrice !==
								selectedVariant.defaultPriceForEveryone && (
								<>
									<FormattedPrice
										price={selectedVariant.defaultPrice}
										currency="NOK"
										variant="discount"
										className="text-muted-foreground text-xl"
									/>
									<Badge variant="destructive">
										-
										{Math.round(
											(1 -
												selectedVariant.defaultPriceForEveryone /
													selectedVariant.defaultPrice) *
												100,
										)}
										%
									</Badge>
								</>
							)}
						<FormattedPrice
							price={selectedVariant.defaultPriceForEveryone}
							currency="NOK"
							className="font-bold text-2xl"
						/>
					</div>

					<Separator />

					<div className="fixed right-0 bottom-0 left-0 flex min-h-24 items-center gap-4 rounded-tl-2xl rounded-tr-2xl bg-background/95 p-4 backdrop-blur-sm supports-backdrop-filter:bg-background/60 md:static md:min-h-min md:rounded-none md:bg-transparent md:p-0 md:backdrop-blur-0 print:hidden">
						<QuantitySelector
							minValue={product.minimumQuantity}
							defaultValue={product.minimumQuantity ?? 1}
							onQuantityChange={(value: number) => setQuantity(value)}
						/>
						<Button
							className="flex-1 bg-primary"
							onClick={() => handleAddToCart(selectedVariant.sku, quantity)}
							disabled={!isPurchaseable || isLoading}
						>
							{isLoading ? (
								<>
									<Loader2 className="h-4 w-4 animate-spin" />
									<span>Legger til i handlekurv...</span>
								</>
							) : (
								<>
									<ShoppingCart className="mr-2 h-4 w-4" /> Legg i handlekurv
								</>
							)}
						</Button>
					</div>
					{product.minimumQuantity && product.minimumQuantity > 1 && (
						<p className="text-muted-foreground text-sm">
							Minimum antall kuverter: {product.minimumQuantity}
						</p>
					)}

					{product.variants.length > 1 && (
						<div className="space-y-4 print:hidden">
							<h2 className="mb-4 text-xl">Velg variant</h2>

							<VariantSelector
								variants={product.variants}
								selectedVariant={selectedVariant}
							/>
						</div>
					)}

					<Accordion
						type="multiple"
						defaultValue={accordionDefaultValues}
						className="print:hidden"
					>
						{selectedVariant.dishes &&
							selectedVariant.dishes?.variants?.length > 0 && (
								<AccordionItem value="dishes">
									<AccordionTrigger>Inkluderte retter</AccordionTrigger>
									<AccordionContent className="flex flex-col gap-4">
										{selectedVariant.dishes?.variants?.map((dish, index) => (
											<div
												key={dish?.sku ?? index}
												className="grid grid-cols-6 items-center gap-2"
											>
												<CrystallizeResponsiveImage
													image={dish?.images?.[0]}
													className="aspect-square h-auto w-24 rounded-lg object-cover"
													sizes="96px"
												/>
												<div className="col-span-5">
													<h3 className="font-medium text-sm md:text-lg">
														{dish?.name}
													</h3>
													{/* Ensure allergens exist before mapping */}
													{dish?.product?.foodProperties?.allergens?.allergens
														?.items?.length > 0 && (
														<p className="text-muted-foreground text-sm">
															Allergener:{" "}
															{dish?.product?.foodProperties?.allergens?.allergens?.items
																.map((allergen) => allergen.name)
																.join(", ")}
														</p>
													)}
												</div>
											</div>
										))}
									</AccordionContent>
								</AccordionItem>
							)}

						{extras && extras.length > 0 && (
							<AccordionItem value="extras">
								<AccordionTrigger>Anbefalt tilbehør</AccordionTrigger>
								<AccordionContent className="flex flex-col gap-4">
									<ProductExtrasSelectorV2 extras={extras} />
								</AccordionContent>
							</AccordionItem>
						)}
						{services && services.length > 0 && (
							<AccordionItem value="services">
								<AccordionTrigger>Tjenester</AccordionTrigger>
								<AccordionContent className="flex flex-col gap-4">
									<ProductExtrasSelector
										extras={services}
										onSelect={handleExtraSelection}
										selectedExtras={selectedExtras}
									/>
								</AccordionContent>
							</AccordionItem>
						)}
						{uniqueAllergens && uniqueAllergens.length > 0 && (
							<AccordionItem value="allergens">
								<AccordionTrigger>Allergener</AccordionTrigger>
								<AccordionContent className="flex flex-col gap-4">
									<div className="grid grid-cols-3 gap-4 md:grid-cols-6">
										{uniqueAllergens.map((allergen) => (
											<div key={allergen.id}>
												<div className="flex flex-col items-center gap-2">
													<div className="flex size-18 items-center justify-center rounded-full bg-primary/40 p-2">
														{allergen.icon && (
															<img
																src={allergen.icon}
																alt={allergen.name}
																className="size-12"
															/>
														)}
													</div>
													<div className="text-center">
														<p>{allergen.name}</p>
													</div>
												</div>
											</div>
										))}
									</div>
								</AccordionContent>
							</AccordionItem>
						)}
					</Accordion>

					<div className="ml-auto flex flex-wrap justify-end gap-2 print:hidden">
						<Button
							type="button"
							variant="outline"
							size="sm"
							className="w-fit text-xs"
							onClick={handleCopyMenuContents}
						>
							{hasCopiedMenuContents ? (
								<Check className="h-3.5 w-3.5" />
							) : (
								<Copy className="h-3.5 w-3.5" />
							)}
							{hasCopiedMenuContents ? "Kopiert" : "Kopier"}
						</Button>
						<Button
							type="button"
							variant="outline"
							size="sm"
							className="w-fit text-xs"
							onClick={() => window.print()}
						>
							<Printer className="h-3.5 w-3.5" />
							Skriv ut
						</Button>
					</div>

					<div className="hidden print:block">
						{selectedVariant.name && selectedVariant.name !== product.name && (
							<section className="product-print-section">
								<h2>Variant</h2>
								<p>{selectedVariant.name}</p>
							</section>
						)}

						{selectedVariant.dishes &&
							selectedVariant.dishes?.variants?.length > 0 && (
								<section className="product-print-section">
									<h2>Inkluderte retter</h2>
									<ul className="product-print-list">
										{selectedVariant.dishes?.variants?.map((dish, index) => (
											<li key={dish?.sku ?? index}>
												<strong>{dish?.name}</strong>
												{dish?.product?.foodProperties?.allergens?.allergens
													?.items?.length > 0 && (
													<span>
														Allergener:{" "}
														{dish?.product?.foodProperties?.allergens?.allergens?.items
															.map((allergen) => allergen.name)
															.join(", ")}
													</span>
												)}
											</li>
										))}
									</ul>
								</section>
							)}

						{uniqueAllergens && uniqueAllergens.length > 0 && (
							<section className="product-print-section">
								<h2>Allergener</h2>
								<p>
									{uniqueAllergens.map((allergen) => allergen.name).join(", ")}
								</p>
							</section>
						)}
					</div>
				</div>
			</div>
		</div>
	);
}
